import { SectionCard } from "@/components/common/SectionCard";
import { primaryBtnClass, ghostBtnClass } from "./serviceShared";
import type { MfgSubscriberDealer } from "@/lib/manufacturing/dealerTypes";
import type { useServiceDefinitionsPanel } from "./useServiceDefinitionsPanel";
import { openManagerProposalCopy } from "@/lib/workspaceModeNavigation";

export function ServiceSetupSalesSection(props: {
  m: ReturnType<typeof useServiceDefinitionsPanel>;
}) {
  const { m } = props;
  const offering = m.activeOffering;
  if (!offering) return null;

  return (
    <SectionCard title="Sales & quoting">
      <p className="mb-3 text-xs text-gray-500 dark:text-zinc-400">
        Slug <code className="font-mono">{offering.slug}</code> — control whether sales can
        quote this service and which subscriber orgs inherit it.
      </p>

      <label className="mb-3 flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
        <input
          type="checkbox"
          checked={m.quotableDraft}
          onChange={(e) => m.setQuotableDraft(e.target.checked)}
        />
        Quotable from sales (adds slug to Opportunity product types)
      </label>

      {m.subscribers.length > 0 ? (
        <SubscriberPicker subscribers={m.subscribers} selected={m.subscriberDraft} onToggle={m.toggleSubscriber} />
      ) : (
        <p className="mb-3 text-xs text-gray-500 dark:text-zinc-400">
          No subscriber orgs on file — link dealers in Manufacturing → Plants to enable per-service
          quoting for subscribers.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={primaryBtnClass}
          disabled={m.actionBusy}
          onClick={() => void m.saveDefinition()}
        >
          {m.actionBusy ? "Saving…" : "Save sales settings"}
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => openManagerProposalCopy(offering.slug)}
        >
          Edit proposal copy in Documents Editor →
        </button>
      </div>
    </SectionCard>
  );
}

function SubscriberPicker(props: {
  subscribers: MfgSubscriberDealer[];
  selected: string[];
  onToggle: (orgId: string) => void;
}) {
  const { subscribers, selected, onToggle } = props;
  return (
    <div className="mb-4">
      <div className="text-sm font-medium text-gray-800 dark:text-zinc-200">
        Available to subscriber orgs
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
        Select which dealer orgs may quote this service through inherited catalog resale.
      </p>
      <ul className="mt-2 space-y-1">
        {subscribers.map((sub) => (
          <li key={sub.dealer_org_id}>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
              <input
                type="checkbox"
                checked={selected.includes(sub.dealer_org_id)}
                onChange={() => onToggle(sub.dealer_org_id)}
              />
              {sub.dealer_org_name}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}
